import React, { useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import Footer from './components/Footer';
import Header from './components/Header';
import MobileBar from './components/MobileBar';
import RequireAuth from './components/RequireAuth';
import ComingSoonGate from './components/ComingSoonGate';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { ComingSoonProvider } from './context/ComingSoonContext';
import { DEFAULT_MAINTENANCE_MESSAGE, getPublicSettings } from './config';
import Catalogue from './pages/Catalogue';
import Confidentialite from './pages/Confidentialite';
import Contact from './pages/Contact';
import Home from './pages/Home';
import MentionsLegales from './pages/MentionsLegales';
import Cgv from './pages/Cgv';
import ProductDetail from './pages/ProductDetail';
import Cart from './pages/Cart';
import CartShared from './pages/CartShared';
import NotFound from './pages/NotFound';
import OrderConfirmation from './pages/OrderConfirmation';
import ResumeOrder from './pages/ResumeOrder';
import Deals from './pages/Deals';
import DealDetail from './pages/DealDetail';
import AccountDashboard from './pages/account/Dashboard';
import AccountLogin from './pages/account/Login';
import AccountPasswordReset from './pages/account/PasswordReset';
import AccountProfile from './pages/account/Profile';
import AccountWishlist from './pages/account/Wishlist';
import AdminDashboard from './pages/admin/Dashboard';
import AdminLogin from './pages/admin/Login';
import AdminOrders from './pages/admin/Orders';
import AdminProductForm from './pages/admin/ProductForm';
import AdminProductImport from './pages/admin/ProductImport';
import AdminProducts from './pages/admin/Products';
import AdminPhotoAudit from './pages/admin/PhotoAudit';
import AdminReservations from './pages/admin/Reservations';
import AdminSuppliers from './pages/admin/Suppliers';
import AdminUserDetail from './pages/admin/UserDetail';
import AdminUsers from './pages/admin/Users';
import AdminWarrantyTiers from './pages/admin/WarrantyTiers';

const DEFAULT_MAINTENANCE = {
  enabled: false,
  message: DEFAULT_MAINTENANCE_MESSAGE,
};

// Exporté pour les tests de routage : ils montent la coquille publique seule, avec
// leur propre routeur, plutôt que l'application entière et ses fournisseurs.
export function PublicShell() {
  const [maintenance, setMaintenance] = useState(DEFAULT_MAINTENANCE);

  useEffect(() => {
    let mounted = true;

    const loadSettings = async () => {
      try {
        const data = await getPublicSettings();
        if (!mounted) return;
        setMaintenance({ ...DEFAULT_MAINTENANCE, ...(data?.maintenance || {}) });
      } catch {
        if (mounted) setMaintenance(DEFAULT_MAINTENANCE);
      }
    };

    loadSettings();
    const intervalId = window.setInterval(loadSettings, 60000);

    return () => {
      mounted = false;
      window.clearInterval(intervalId);
    };
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <Header maintenance={maintenance} />
      <main className="flex-1 main-content">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/deals" element={<Deals />} />
          <Route path="/deals/:id" element={<DealDetail />} />
          <Route path="/catalogue" element={<Catalogue />} />
          <Route path="/produit/:id" element={<ProductDetail maintenance={maintenance} />} />
          {/* Panier client (sans paramètre) — distinct du panier commercial
              partagé ci-dessous, qui porte un identifiant dans l'URL. */}
          <Route path="/panier" element={<Cart maintenance={maintenance} />} />
          <Route path="/panier/:cartUuid" element={<CartShared />} />
          <Route path="/reprendre-commande/:token" element={<ResumeOrder />} />
          {/* Retour de paiement : URL de retour PayPal (`payment_service.
              create_reservation_deposit_paypal`) ET de retour Stripe 3DS
              (`SpecialOrderModal`). Elle n'était déclarée nulle part : le client
              atterrissait sur une zone vide entre l'en-tête et le pied de page. */}
          <Route path="/commande/confirmation" element={<OrderConfirmation />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/mentions-legales" element={<MentionsLegales />} />
          <Route path="/cgv" element={<Cgv />} />
          <Route path="/confidentialite" element={<Confidentialite />} />
          {/* Repli, en DERNIER : une URL inconnue ne doit plus rendre une page vide,
              indiscernable d'une panne. */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <Footer />
      <MobileBar />
    </div>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/my" element={<AdminLogin />} />
      <Route path="/admin" element={<Navigate to="/my" replace />} />
      <Route path="/compte/connexion" element={<AccountLogin mode="login" />} />
      <Route path="/compte/inscription" element={<AccountLogin mode="signup" />} />
      <Route path="/compte/mot-de-passe-oublie" element={<AccountPasswordReset />} />
      <Route path="/compte/reset" element={<AccountPasswordReset />} />

      <Route
        element={
          <RequireAuth
            redirectTo="/my"
            requiredPermission="dashboard:view"
            fallbackTo="/compte"
          />
        }
      >
        <Route path="/my/dashboard" element={<AdminDashboard />} />
        <Route path="/my/produits" element={<AdminProducts />} />
        <Route path="/my/photo-audit" element={<AdminPhotoAudit />} />
        <Route path="/my/reservations" element={<AdminReservations />} />
        <Route path="/my/commandes" element={<AdminOrders />} />
        <Route path="/my/utilisateurs" element={<AdminUsers />} />
        <Route path="/my/utilisateurs/:id" element={<AdminUserDetail />} />
        <Route path="/my/fournisseurs" element={<AdminSuppliers />} />
        {/* Réglage du barème de la Garantie Atelier (Task 7 panier V2) — même
            groupe que les autres écrans d'administration : la permission fine
            (`maintenance:view`/`maintenance:update`) est vérifiée DANS la page,
            pour que le lecteur seul y voie un barème en lecture seule plutôt
            qu'un écran cassé (cf. `AdminWarrantyTiers`). */}
        <Route path="/my/garantie" element={<AdminWarrantyTiers />} />
      </Route>

      <Route
        element={
          <RequireAuth
            redirectTo="/my"
            requiredPermission="products:manage"
            fallbackTo="/my/produits"
          />
        }
      >
        <Route path="/my/produits/nouveau" element={<AdminProductForm />} />
        <Route path="/my/produits/importer" element={<AdminProductImport />} />
        <Route path="/my/produits/:id/modifier" element={<AdminProductForm />} />
      </Route>

      <Route
        element={
          <RequireAuth
            redirectTo="/compte/connexion"
            requiredPermission="client_portal:view"
          />
        }
      >
        <Route path="/compte/profil" element={<AccountProfile />} />
      </Route>

      <Route
        element={
          <RequireAuth
            redirectTo="/compte/connexion"
            requiredPermission="client_portal:view"
            profileGate
          />
        }
      >
        <Route path="/compte" element={<AccountDashboard />} />
        <Route path="/compte/favoris" element={<AccountWishlist />} />
      </Route>

      <Route path="/*" element={<PublicShell />} />
    </Routes>
  );
}

// Remonte en haut de page à chaque changement de route. Sans ça, React conserve
// la position de scroll précédente → on ouvrait une fiche produit « au milieu ».
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

export default function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <AuthProvider>
        <CartProvider>
          <ComingSoonProvider>
            <ComingSoonGate>
              <AppRoutes />
            </ComingSoonGate>
          </ComingSoonProvider>
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
